/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { FieldPath, UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableCell } from "@/components/ui/table";

import { Row } from "@/components/Row";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

export const TableCellEnumSelect = <T extends z.ZodTypeAny>({
  className,
  disabled,
  fieldName,
  form,
  handleOnBlur,
  index,
  listName,
  options,
  rowId,
  statusToDotColor,
  type,
}: {
  className?: string;
  disabled?: boolean;
  fieldName: string;
  form: UseFormReturn<z.infer<T>>;
  handleOnBlur: (
    id: number,
    fieldName: string,
    updatedValue: string | null,
    type?: "date" | "number",
  ) => void;
  index: number;
  listName: "interviews" | "placements" | "requisitions";
  options: {
    disabled?: boolean;
    value: string;
    label: string;
  }[];
  rowId: number;
  statusToDotColor?: Record<string, string>;
  type?: "number";
}) => {
  const fieldNamePath = `${listName}.${index}.${fieldName}` as FieldPath<
    z.infer<T>
  >;

  return (
    <TableCell className="p-1">
      <FormField
        control={form.control}
        name={fieldNamePath}
        render={({ field }) => {
          const displayValue =
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            (field.value as string | number | null)?.toString() || "-";
          return (
            <FormItem>
              <FormControl>
                <Select
                  disabled={disabled}
                  onValueChange={(v) => {
                    field.onChange(v);
                    handleOnBlur(rowId, fieldName, v, type);
                    mixpanelUtils.trackInlineEdit(
                      fieldName,
                      getScreenFromPathname(location.pathname),
                    );
                  }}
                  value={displayValue}
                >
                  <SelectTrigger
                    id={`cell-${fieldName}-${index}`}
                    className={cn(
                      "rounded-sm border-transparent bg-transparent hover:border-slate-300 focus:border-slate-300",
                      className,
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((option) => (
                      <SelectItem
                        key={option.value}
                        disabled={option.disabled}
                        value={option.value}
                      >
                        {statusToDotColor ? (
                          <Row className="items-center gap-2">
                            <div
                              className={cn(
                                "h-2 w-2 rounded-full",
                                statusToDotColor[option.value],
                              )}
                            />
                            <span>{option.label}</span>
                          </Row>
                        ) : (
                          option.label
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          );
        }}
      />
    </TableCell>
  );
};
